import { useEffect, useMemo, useState } from 'react';

import {
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  makeStyles,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';

import SearchBar from 'components/SearchBar';
import useCurrentUser from 'hooks/useCurrentUser';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchSelectAtendentes,
  UsuarioInterfaceV2,
  usuariosSelector,
} from 'store/reducers/usuarios';

const useStyles = makeStyles(({ palette }) => ({
  avatarOffline: {
    border: `2px solid ${palette.error.main}`,
  },
  avatarOnline: {
    border: `2px solid ${palette.success.main}`,
  },
}));

type ListaAtendentesProps = {
  onSelect: (atendente: UsuarioInterfaceV2) => void;
  hideCurrentUser?: boolean;
};

export default function ListaAtendentes({
  onSelect,
  hideCurrentUser = true,
}: ListaAtendentesProps) {
  const dispatch = useAppDispatch();
  const { selectAtendentes, loadingSelectAtendentes } =
    useAppSelector(usuariosSelector);
  const currentUser = useCurrentUser();

  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    dispatch(fetchSelectAtendentes());
  }, [dispatch]);

  const filteredAtendentes = useMemo(() => {
    let list = selectAtendentes;

    if (hideCurrentUser && currentUser) {
      list = list.filter(atendente => atendente.id !== currentUser.id);
    }

    if (!searchTerm.trim()) return list;

    const normalizedSearch = searchTerm.toLowerCase();

    return list.filter(
      atendente =>
        atendente.name.toLowerCase().includes(normalizedSearch) ||
        atendente.email.toLowerCase().includes(normalizedSearch),
    );
  }, [searchTerm, selectAtendentes, hideCurrentUser, currentUser]);

  const items = loadingSelectAtendentes
    ? Array.from(new Array(3) as UsuarioInterfaceV2[])
    : filteredAtendentes;

  const renderAtendentes = items.map((atendente, index) => {
    const key = atendente ? atendente.id : index;

    if (atendente) {
      return (
        <ListItem
          disableGutters
          key={key}
          onClick={() => onSelect(atendente)}
          button
          style={{ padding: 0, paddingLeft: 8 }}
        >
          <ListItemIcon style={{ minWidth: 48 }}>
            <Avatar
              src={atendente.foto}
              className={clsx({
                [classes.avatarOffline]: !atendente.isOnline,
                [classes.avatarOnline]: atendente.isOnline,
              })}
            />
          </ListItemIcon>
          <ListItemText
            primary={atendente.name}
            secondary={atendente.email}
            primaryTypographyProps={{
              variant: 'body2',
            }}
            secondaryTypographyProps={{
              variant: 'caption',
            }}
          />
        </ListItem>
      );
    }

    return (
      <ListItem key={key}>
        <ListItemAvatar>
          <Skeleton variant="circle">
            <Avatar />
          </Skeleton>
        </ListItemAvatar>
        <ListItemText primary={<Skeleton variant="rect" />} />
      </ListItem>
    );
  });

  return (
    <>
      <SearchBar
        placeholder="Buscar atendente..."
        onChange={value => setSearchTerm(value)}
        onCancelSearch={() => setSearchTerm('')}
      />

      <List style={{ padding: 0 }}>
        <ListItem disableGutters>
          <ListItemText secondary="Selecione o atendente que você deseja transferir." />
        </ListItem>

        {renderAtendentes}

        {!loadingSelectAtendentes && filteredAtendentes.length === 0 && (
          <ListItem disableGutters>
            <ListItemText secondary="Nenhum atendente encontrado para esta busca." />
          </ListItem>
        )}
      </List>
    </>
  );
}
